import { useEffect, useRef } from 'react';

export interface KeyboardNavigationOptions {
  isOpen: boolean;
  itemsLength: number;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  onSelect: () => void;
  onClose: () => void;
  onOpen?: () => void;
}

/**
 * Custom hook for keyboard navigation in dropdown-like components
 * Handles ArrowUp/Down, Enter, Escape keys and auto-scrolling
 */
export const useKeyboardNavigation = ({
  isOpen,
  itemsLength,
  activeIndex,
  setActiveIndex,
  onSelect,
  onClose,
  onOpen,
}: KeyboardNavigationOptions) => {
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!listRef.current) return;

    const activeElement = listRef.current.querySelector<HTMLElement>(
      `[data-index='${activeIndex}']`
    );

    if (activeElement) {
      activeElement.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!isOpen && ['ArrowDown', 'Enter'].includes(e.key)) {
      onOpen?.();
      e.preventDefault();
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(Math.min(activeIndex + 1, itemsLength - 1));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(Math.max(activeIndex - 1, 0));
        break;

      case 'Enter':
        e.preventDefault();
        onSelect();
        break;

      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  return {
    listRef,
    handleKeyDown,
  };
};
